import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  CalendarDays,
  ChartBar,
  FileText,
  LayoutGrid,
  LayoutList,
  Search,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  useChangeTemplateStatus,
  useCloneTemplate,
  useDeleteTemplate,
  useTemplateList,
  useTemplateSummary,
} from "../../hooks";
import { EmptyState } from "./EmptyState";
import { TemplatesList } from "./TemplatesList";

const BillTemplate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const { data: summary } = useTemplateSummary();
  const cloneTemplate = useCloneTemplate();
  const changeTemplateStatus = useChangeTemplateStatus();
  const deleteTemplate = useDeleteTemplate();

  // KPI Cards configuration using QuickStatsCard
  const templateKpiCards = [
  {
    title: "Total Templates",
    tooltip: "Total number of bill templates available in the system.",
    count: summary?.totalTemplates || 0,
    trendSummary: "Available bill templates in the system",
    trendDirection: "up" as const,
    icon: <FileText className="h-8 w-8 text-sky-500" />,
  },
  {
    title: "Active Templates",
    tooltip: "Templates currently active and being used for billing.",
    count: summary?.totalActiveTemplates || 0,
    trendSummary: "Templates currently in use",
    trendDirection: "up" as const,
    icon: <Users className="h-8 w-8 text-green-500" />,
  },
  {
    title: "Average Usage",
    tooltip: "Average number of times a template is used across the system.",
    count: summary?.avgTemplateUsage || 0,
    trendSummary: "Average usage per template",
    trendDirection: "up" as const,
    icon: <ChartBar className="h-8 w-8 text-purple-500" />,
  },
  {
    title: "Newest Template",
    tooltip: "Date when the most recently created template was added.",
    count: summary?.newestTemplateDate || "N/A",
    trendSummary: "Most recently created template",
    trendDirection: "up" as const,
    icon: <CalendarDays className="h-8 w-8 text-amber-500" />,
  },
];

  const handleToggleStatus = (id: string, isActive: boolean) => {
    changeTemplateStatus.mutate(
      { id, isActive },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [QueryKeyFactory.module.bx.billTemplate.templateList()],
          });
        },
      }
    );
  };

  const handleEditTemplate = (id: string, templateId: string) => {
    navigate(`template-setup/${id}/${templateId}`);
  };

  const handleCloneTemplate = (id: string) => {
    cloneTemplate.mutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeyFactory.module.bx.billTemplate.templateList()],
        });
      },
    });
  };

  const handleDeleteTemplate = (id: string) => {
    deleteTemplate.mutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeyFactory.module.bx.billTemplate.templateList()],
        });
      },
    });
  };

  const { data: templates } = useTemplateList(filterStatus, searchQuery);

  return (
    <div className="">
      <header className="">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="page-title">
                Bill Templates
              </h1>
            </div>
            <p className="page-subtitle">
              Design and manage bill formats to standardize consumer billing
              documents.
            </p>
          </div>

          <Button
            onClick={() => navigate("template-setup")}
          >
            Create New Template
          </Button>
        </div>
      </header>

      <main className="py-4">
        {/* KPI Summary Cards - Using loop with QuickStatsCard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {templateKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
              tooltip={card.tooltip}
            />
          ))}
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <Input
              placeholder="Search by template name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <div className="flex gap-4 items-center w-full md:w-auto">
            <div className="w-full md:w-48">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex border rounded-md">
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${viewMode === "list" ? "bg-muted" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <LayoutList className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${viewMode === "grid" ? "bg-muted" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {templates?.length === 0 &&
        searchQuery === "" &&
        filterStatus === "all" ? (
          <EmptyState onCreateNew={() => navigate("/template/new")} />
        ) : templates?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              No templates found matching your search criteria.
            </p>
            <Button
              variant="link"
              onClick={() => {
                setSearchQuery("");
                setFilterStatus("all");
              }}
              className="mt-2"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <TemplatesList
            templateList={templates}
            viewMode={viewMode}
            onToggleStatus={handleToggleStatus}
            onEdit={handleEditTemplate}
            onClone={handleCloneTemplate}
            onDelete={handleDeleteTemplate}
          />
        )}
      </main>
    </div>
  );
};

export default BillTemplate;
